import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Apple, Download, Save, Smartphone } from 'lucide-react';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { api, apiError } from '@/lib/api-client';
import { useUiStore } from '@/stores/uiStore';

type PlatformLinks = {
  preview: string;
  production: string;
  store: string;
};

type DownloadLinks = {
  android: PlatformLinks;
  ios: PlatformLinks;
};

const EMPTY_PLATFORM: PlatformLinks = { preview: '', production: '', store: '' };

export function DownloadSettingsPage() {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);

  const [android, setAndroid] = useState<PlatformLinks>(EMPTY_PLATFORM);
  const [ios, setIos] = useState<PlatformLinks>(EMPTY_PLATFORM);

  const linksQ = useQuery<DownloadLinks>({
    queryKey: ['settings', 'download'],
    queryFn: () => api.get('settings/download').json<DownloadLinks>(),
  });

  // Sync server values into form state on (re)load.
  useEffect(() => {
    if (!linksQ.data) return;
    setAndroid({
      preview: linksQ.data.android?.preview ?? '',
      production: linksQ.data.android?.production ?? '',
      store: linksQ.data.android?.store ?? '',
    });
    setIos({
      preview: linksQ.data.ios?.preview ?? '',
      production: linksQ.data.ios?.production ?? '',
      store: linksQ.data.ios?.store ?? '',
    });
  }, [linksQ.data]);

  const save = useMutation({
    mutationFn: () =>
      api
        .put('settings/download', {
          json: {
            android: {
              preview: android.preview.trim(),
              production: android.production.trim(),
              store: android.store.trim(),
            },
            ios: {
              preview: ios.preview.trim(),
              production: ios.production.trim(),
              store: ios.store.trim(),
            },
          },
        })
        .json<DownloadLinks>(),
    onSuccess: (next) => {
      qc.setQueryData(['settings', 'download'], next);
      qc.invalidateQueries({ queryKey: ['settings', 'download'] });
      pushToast({ variant: 'success', message: t('admin:download.saveSuccess') });
    },
    onError: async (e) => {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    },
  });

  const renderPlatformBlock = (
    platform: 'android' | 'ios',
    state: PlatformLinks,
    setState: (v: PlatformLinks) => void,
    title: string,
    Icon: typeof Smartphone,
    placeholders: PlatformLinks,
  ) => (
    <Card>
      <CardHeader>
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
          <Icon className="h-4 w-4" /> {title}
        </h2>
      </CardHeader>
      <CardBody>
        <div className="space-y-3">
          <FormField
            label={t('admin:download.previewLabel')}
            htmlFor={`dl-${platform}-preview`}
            hint={t('admin:download.previewHint')}
          >
            <Input
              id={`dl-${platform}-preview`}
              type="url"
              value={state.preview}
              onChange={(e) => setState({ ...state, preview: e.target.value })}
              placeholder={placeholders.preview}
              autoComplete="off"
            />
          </FormField>
          <FormField
            label={t('admin:download.productionLabel')}
            htmlFor={`dl-${platform}-production`}
            hint={t('admin:download.productionHint')}
          >
            <Input
              id={`dl-${platform}-production`}
              type="url"
              value={state.production}
              onChange={(e) => setState({ ...state, production: e.target.value })}
              placeholder={placeholders.production}
              autoComplete="off"
            />
          </FormField>
          <FormField
            label={t('admin:download.storeLabel')}
            htmlFor={`dl-${platform}-store`}
            hint={t('admin:download.storeHint')}
          >
            <Input
              id={`dl-${platform}-store`}
              type="url"
              value={state.store}
              onChange={(e) => setState({ ...state, store: e.target.value })}
              placeholder={placeholders.store}
              autoComplete="off"
            />
          </FormField>
        </div>
      </CardBody>
    </Card>
  );

  return (
    <div className="animate-fade-in mx-auto max-w-3xl space-y-6 py-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-forest-100 text-forest-700">
          <Download className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            {t('admin:download.title')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('admin:download.subtitle')}
          </p>
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        className="space-y-6"
      >
        {renderPlatformBlock(
          'android',
          android,
          setAndroid,
          t('admin:download.androidTitle'),
          Smartphone,
          {
            preview: 'https://expo.dev/.../forest-mobile-preview.apk',
            production: 'https://expo.dev/.../forest-mobile-prod.apk',
            store: 'https://play.google.com/store/apps/details?id=md.forest.mobile',
          },
        )}

        {renderPlatformBlock(
          'ios',
          ios,
          setIos,
          t('admin:download.iosTitle'),
          Apple,
          {
            preview: 'https://testflight.apple.com/join/...',
            production: 'https://apps.apple.com/.../id...',
            store: 'https://apps.apple.com/.../id...',
          },
        )}

        <div className="flex justify-end">
          <Button type="submit" loading={save.isPending}>
            <Save className="mr-1 h-4 w-4" />
            {t('admin:download.save')}
          </Button>
        </div>
      </form>
    </div>
  );
}
